<script setup lang="ts">
import BackstageImage from '@/components/common/BackstageImage.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import { getBoxImageProxyUrl } from '@/composables/utils';
import { VISIBLE_STOCK_MATRIX_TYPES } from '@/constants/order.constants';
import { MatrixOrder } from '@/models/matrix-order.model';
import { MatrixType } from '@/models/matrix-type.enum';
import { Availability } from '@/types/matrix.enums';
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

export interface Props {
  matrixOrders: MatrixOrder[];
  matrixType: string;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<{
  proceed: [];
}>();

const matrixProducts = computed(() => props.matrixOrders.flatMap((matrixOrder) => matrixOrder.matrixProducts));
const noStockProducts = computed(() =>
  matrixProducts.value.filter(
    (product) =>
      product.total > 0 &&
      (product.availabilityInfo?.availabilityText === `${t('config.no_availability_label')}` ||
        product.availability === Availability.outOfStock),
  ),
);
const veryLowStockProducts = computed(() =>
  matrixProducts.value.filter(
    (product) =>
      product.total > 0 &&
      (product.availabilityInfo?.availabilityText === `${t('config.very_low_availability_label')}` ||
        product.availability === Availability.veryLowStock),
  ),
);

const highRiskCategories = computed(() => {
  let sections = [];

  const veryLowStockLength = veryLowStockProducts.value.length;
  const noLowStockLength = noStockProducts.value.length;

  if (veryLowStockLength > 0) {
    sections.push({
      title: t('viewOrder.confirmation_modal.very_low_stock_title', veryLowStockLength),
      products: veryLowStockProducts.value,
    });
  }
  if (noLowStockLength > 0) {
    sections.push({
      title: t('viewOrder.confirmation_modal.no_stock_title', noLowStockLength),
      products: noStockProducts.value,
    });
  }

  return sections;
});

onMounted(() => {
  // If there are no products to show and the matrix type is not in the visible list, proceed automatically
  if (highRiskCategories.value.length === 0 || !VISIBLE_STOCK_MATRIX_TYPES.includes(props.matrixType as MatrixType)) {
    emit('proceed');
  }
});
</script>

<template>
  <NotificationMessage :show-close-button="false" class="high-risk-modal" :size="'large'">
    <div class="high-risk-modal--sections">
      <div v-for="(section, sectionIndex) in highRiskCategories" :key="sectionIndex" class="high-risk-section">
        <div class="high-risk-section--title">
          {{ section.title }}
        </div>
        <div class="high-risk-section--body">
          <div v-for="(product, i) in section.products" :key="i" class="high-risk-section--body-card">
            <div class="high-risk-section--body-card-img">
              <BackstageImage
                class="high-risk-section--body-card-img-content"
                :src="getBoxImageProxyUrl(product.picUrl)"
                :alt="`${product.productName}`" />
            </div>
            <div class="high-risk-section--body-card-text">
              <span class="product-name">{{ product.productGroupName }}</span>
              <div class="product-info">
                <div class="product-color-group">{{ product.color }} ({{ product.productGroupNumber }})</div>
                <div class="dot"></div>
                <div class="product-size">
                  {{ $t('viewOrder.confirmation_modal.size') }}: <span v-if="product.sizeId">{{ product.sizeId }}</span>
                </div>
                <div class="dot"></div>
                <div class="product-quantity">
                  {{ $t('viewOrder.confirmation_modal.quantity') }}:
                  <span v-if="product.total">{{ product.total }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="high-risk-modal--actions">
      <slot></slot>
    </div>
  </NotificationMessage>
</template>

<style scoped lang="scss">
.high-risk-modal {
  &--sections > *:not(:last-child) {
    padding-bottom: 1rem;
    border-bottom: 2px solid var(--shade-3);
  }

  &--actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
    gap: 1rem;
  }

  .high-risk-section {
    &--title {
      font-size: 20px;
      font-weight: 700;
      line-height: 30px;
      margin: 1rem 0;
      text-align: left;
    }

    &--body {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-height: 15rem;
      overflow-y: auto;

      &-card {
        display: flex;
        gap: 0.5rem;
        padding: 0.5rem;
        &-img {
          width: 5vw;
          &-content {
            padding: 0.3rem;
            height: 45px;
          }
        }

        &-text {
          display: grid;
          gap: 0.1rem;
          width: 100%;
          text-align: left;
          color: var(--shade-4);
        }
      }
    }
  }

  .divider {
    margin-top: 2rem;
    border-radius: 100px;
    border-top: 2px solid var(--shade-2);
  }

  .dot {
    width: 5px;
    height: 5px;
    margin: 0 0.5rem;
    border-radius: 50%;
    display: inline-block;
    background-color: var(--shade-5);
  }

  .product-name {
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: var(--primary-text-color);
  }

  .product-info {
    display: flex;
    align-items: center;
  }

  .product-color-group {
    flex-grow: 1;
    white-space: nowrap;
    &:hover {
      white-space: normal;
    }
  }

  .product-size {
    width: 6rem;
    @include breakpoint(large) {
      width: 6rem;
    }
  }

  .product-quantity {
    width: 8rem;
    justify-content: center;
    @include breakpoint(xlarge) {
      width: 10rem;
    }
  }
}
</style>
